import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Grid,
  Tooltip,
  LinearProgress,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Divider
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  AutoAwesome as AutoIcon
} from '@mui/icons-material';
import api from '../../services/api';

interface ExtensionQuota {
  ExtID: number;
  CustID: number;
  extension: string;
  Active: number;
  Department: string;
  assigned_to: string;
  user_email: string;
  quota_dids: number | null;
  quota_sms: number | null;
  quota_mms: number | null;
  quota_softphones: number | null;
  quota_storage_gb: number | null;
  quota_inherited: boolean;
  reassignment_count: number;
  last_reassignment: string | null;
  sms_used: number;
  mms_used: number;
  storage_used_gb: number;
  dids_active: number;
  softphones_active: number;
  sms_overage: number;
  mms_overage: number;
  did_overage: number;
}

interface PackageData {
  customer_id: number;
  company_name: string;
  allocation_mode: 'global' | 'quota' | 'hybrid';
  maxDIDs: number;
  maxExtensions: number;
  maxSoftphones: number;
  sms_included: number;
  mms_included: number;
  default_quota_dids: number;
  default_quota_sms: number;
  default_quota_mms: number;
  default_quota_softphones: number;
  default_quota_storage_gb: number;
}

const ExtensionsQuota: React.FC = () => {
  const [extensions, setExtensions] = useState<ExtensionQuota[]>([]);
  const [packageData, setPackageData] = useState<PackageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<Partial<ExtensionQuota>>({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(null);
  const [customers, setCustomers] = useState<any[]>([]);

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    if (selectedCustomer) {
      loadExtensions();
    }
  }, [selectedCustomer]);

  const loadCustomers = async () => {
    try {
      const response = await api.get('/endpoints/customers.php');
      // API returns data in response.data.data, not response.data.customers
      const customersList = response.data.data || response.data.customers || [];
      setCustomers(customersList);
      if (customersList.length > 0) {
        // Use 'id' not 'CustID' based on API response
        setSelectedCustomer(customersList[0].id || customersList[0].CustID);
      }
    } catch (err: any) {
      console.error('Failed to load customers:', err);
      setError('Failed to load customers');
    }
  };

  const loadExtensions = async () => {
    if (!selectedCustomer) return;

    try {
      setLoading(true);
      const response = await api.get('/endpoints/extensions-quota.php', {
        params: { path: 'list', custid: selectedCustomer }
      });

      // API returns data wrapped in data.data
      const responseData = response.data.data || response.data;
      setExtensions(responseData.extensions || []);
      setPackageData(responseData.package || null);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load extensions');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (extension: ExtensionQuota) => {
    setEditingId(extension.ExtID);
    setEditValues({
      quota_dids: extension.quota_dids,
      quota_sms: extension.quota_sms,
      quota_mms: extension.quota_mms,
      quota_softphones: extension.quota_softphones,
      quota_storage_gb: extension.quota_storage_gb
    });
  };

  const handleSave = async () => {
    if (!editingId) return;

    try {
      await api.put('/endpoints/extensions-quota.php?path=update', {
        extId: editingId,
        ...editValues
      });

      await loadExtensions();
      setEditingId(null);
      setEditValues({});
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update quotas');
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditValues({});
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleApplyDefaults = async () => {
    handleMenuClose();
    if (!selectedCustomer) return;

    try {
      await api.post('/endpoints/extensions-quota.php?path=apply-defaults', {
        custId: selectedCustomer
      });
      await loadExtensions();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to apply defaults');
    }
  };

  const handleDistributeEvenly = async () => {
    handleMenuClose();
    if (!selectedCustomer) return;

    try {
      await api.post('/endpoints/extensions-quota.php?path=distribute-evenly', {
        custId: selectedCustomer
      });
      await loadExtensions();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to distribute resources');
    }
  };

  const getUsageColor = (used: number, quota: number | null): string => {
    if (!quota) return 'inherit';
    const percentage = (used / quota) * 100;
    if (percentage > 100) return 'error.main';
    if (percentage > 80) return 'warning.main';
    return 'success.main';
  };

  const getUsageBar = (used: number, quota: number | null) => {
    if (!quota) return null;
    const percentage = Math.min(100, (used / quota) * 100);

    return (
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flexGrow: 1 }}>
          <LinearProgress
            variant="determinate"
            value={percentage}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                backgroundColor: percentage > 100 ? 'error.main' :
                                percentage > 80 ? 'warning.main' : 'success.main'
              }
            }}
          />
        </Box>
        <Typography variant="caption" sx={{ minWidth: 60 }}>
          {used}/{quota}
        </Typography>
      </Box>
    );
  };

  const calculateTotalOverages = () => {
    const totals = extensions.reduce(
      (acc, ext) => ({
        didOverage: acc.didOverage + (ext.did_overage || 0),
        smsOverage: acc.smsOverage + (ext.sms_overage || 0),
        mmsOverage: acc.mmsOverage + (ext.mms_overage || 0),
      }),
      { didOverage: 0, smsOverage: 0, mmsOverage: 0 }
    );

    return totals;
  };

  if (!selectedCustomer && customers.length > 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6">Select a customer to view extensions</Typography>
        </CardContent>
      </Card>
    );
  }

  const overages = calculateTotalOverages();

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Extensions Quota Management (New)</Typography>
        <Stack direction="row" spacing={2}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Customer</InputLabel>
            <Select
              value={selectedCustomer || ''}
              onChange={(e) => setSelectedCustomer(Number(e.target.value))}
              label="Customer"
            >
              {customers.map((customer: any) => (
                <MenuItem key={customer.id || customer.CustID} value={customer.id || customer.CustID}>
                  {customer.companyName || customer.company_name || 'Unnamed'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadExtensions}
            variant="outlined"
          >
            Refresh
          </Button>
          <IconButton onClick={handleMenuOpen}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleApplyDefaults}>
              <AutoIcon sx={{ mr: 1 }} /> Apply Package Defaults
            </MenuItem>
            <MenuItem onClick={handleDistributeEvenly}>
              <AutoIcon sx={{ mr: 1 }} /> Distribute Evenly
            </MenuItem>
          </Menu>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      {packageData && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Allocation Mode
                </Typography>
                <Typography variant="h5">
                  {packageData.allocation_mode || 'Global'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Active Extensions
                </Typography>
                <Typography variant="h5">
                  {extensions.filter(e => e.Active === 1).length} / {packageData.maxExtensions}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total DIDs Allocated
                </Typography>
                <Typography variant="h5" color={overages.didOverage > 0 ? 'error' : 'inherit'}>
                  {extensions.reduce((sum, e) => sum + (e.quota_dids || 0), 0)} / {packageData.maxDIDs}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Overages
                </Typography>
                <Stack direction="row" spacing={1}>
                  {overages.didOverage > 0 && (
                    <Chip label={`DIDs: ${overages.didOverage}`} color="error" size="small" />
                  )}
                  {overages.smsOverage > 0 && (
                    <Chip label={`SMS: ${overages.smsOverage}`} color="error" size="small" />
                  )}
                  {overages.mmsOverage > 0 && (
                    <Chip label={`MMS: ${overages.mmsOverage}`} color="error" size="small" />
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Extensions Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Extension</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">DIDs</TableCell>
              <TableCell align="center">SMS</TableCell>
              <TableCell align="center">MMS</TableCell>
              <TableCell align="center">Softphones</TableCell>
              <TableCell align="center">Storage (GB)</TableCell>
              <TableCell align="center">Reassignments</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <LinearProgress />
                </TableCell>
              </TableRow>
            ) : extensions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  No extensions found
                </TableCell>
              </TableRow>
            ) : (
              extensions.map((ext) => (
                <TableRow key={ext.ExtID}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {ext.extension}
                    </Typography>
                    {ext.Department && (
                      <Typography variant="caption" color="text.secondary">
                        {ext.Department}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{ext.assigned_to}</Typography>
                    {ext.user_email && (
                      <Typography variant="caption" color="text.secondary">
                        {ext.user_email}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ext.Active ? 'Active' : 'Inactive'}
                      color={ext.Active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {editingId === ext.ExtID ? (
                      <TextField
                        size="small"
                        type="number"
                        value={editValues.quota_dids || ''}
                        onChange={(e) => setEditValues({
                          ...editValues,
                          quota_dids: Number(e.target.value)
                        })}
                        sx={{ width: 80 }}
                      />
                    ) : (
                      <Box>
                        {getUsageBar(ext.dids_active, ext.quota_dids)}
                        {ext.did_overage > 0 && (
                          <Chip
                            label={`+${ext.did_overage}`}
                            color="error"
                            size="small"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {editingId === ext.ExtID ? (
                      <TextField
                        size="small"
                        type="number"
                        value={editValues.quota_sms || ''}
                        onChange={(e) => setEditValues({
                          ...editValues,
                          quota_sms: Number(e.target.value)
                        })}
                        sx={{ width: 80 }}
                      />
                    ) : (
                      <Box>
                        {getUsageBar(ext.sms_used, ext.quota_sms)}
                        {ext.sms_overage > 0 && (
                          <Chip
                            label={`+${ext.sms_overage}`}
                            color="error"
                            size="small"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {editingId === ext.ExtID ? (
                      <TextField
                        size="small"
                        type="number"
                        value={editValues.quota_mms || ''}
                        onChange={(e) => setEditValues({
                          ...editValues,
                          quota_mms: Number(e.target.value)
                        })}
                        sx={{ width: 80 }}
                      />
                    ) : (
                      <Box>
                        {getUsageBar(ext.mms_used, ext.quota_mms)}
                        {ext.mms_overage > 0 && (
                          <Chip
                            label={`+${ext.mms_overage}`}
                            color="error"
                            size="small"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {editingId === ext.ExtID ? (
                      <TextField
                        size="small"
                        type="number"
                        value={editValues.quota_softphones || ''}
                        onChange={(e) => setEditValues({
                          ...editValues,
                          quota_softphones: Number(e.target.value)
                        })}
                        sx={{ width: 80 }}
                      />
                    ) : (
                      <Typography>{ext.quota_softphones || 0}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {editingId === ext.ExtID ? (
                      <TextField
                        size="small"
                        type="number"
                        value={editValues.quota_storage_gb || ''}
                        onChange={(e) => setEditValues({
                          ...editValues,
                          quota_storage_gb: Number(e.target.value)
                        })}
                        sx={{ width: 80 }}
                      />
                    ) : (
                      getUsageBar(ext.storage_used_gb, ext.quota_storage_gb)
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {ext.reassignment_count > 1 ? (
                      <Tooltip title={`Last: ${ext.last_reassignment}`}>
                        <Chip
                          label={ext.reassignment_count}
                          color="warning"
                          size="small"
                          icon={<WarningIcon />}
                        />
                      </Tooltip>
                    ) : (
                      <Typography color="text.secondary">
                        {ext.reassignment_count}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingId === ext.ExtID ? (
                      <Stack direction="row" spacing={1}>
                        <IconButton size="small" onClick={handleSave} color="primary">
                          <SaveIcon />
                        </IconButton>
                        <IconButton size="small" onClick={handleCancel}>
                          <CancelIcon />
                        </IconButton>
                      </Stack>
                    ) : (
                      <IconButton size="small" onClick={() => handleEdit(ext)}>
                        <EditIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer Info */}
      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
        <Chip
          icon={<CheckIcon />}
          label="Inherited from package"
          size="small"
          variant="outlined"
        />
        <Chip
          icon={<WarningIcon />}
          label="Multiple reassignments"
          size="small"
          color="warning"
          variant="outlined"
        />
        <Chip
          icon={<ErrorIcon />}
          label="Over quota"
          size="small"
          color="error"
          variant="outlined"
        />
      </Box>
    </Box>
  );
};

export default ExtensionsQuota;